import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { navigationByPath } from '@/config/navigation';

const HOME_PATH = '/dashboard';
const MAX_OPEN_TABS = 12;
const MAX_RECENT_TABS = 6;

interface NavigationState {
  openPaths: string[];
  recentlyClosed: string[];
  refreshTokens: Record<string, number>;
  openTab: (path: string) => void;
  refreshTab: (path: string) => void;
  closeTab: (path: string) => void;
  closeLeft: (path: string) => void;
  closeRight: (path: string) => void;
  closeOthers: (path: string) => void;
  closeAll: () => void;
  reopenLastClosed: () => string | null;
  moveTab: (path: string, direction: 'left' | 'right') => void;
  pruneTabs: (allowedPaths: string[]) => void;
}

function isKnownPath(path: string) {
  return navigationByPath.has(path);
}

export const useNavigationStore = create<NavigationState>()(
  persist(
    (set, get) => ({
      openPaths: [HOME_PATH],
      recentlyClosed: [],
      refreshTokens: {},
      openTab: (path) => {
        if (!isKnownPath(path)) return;
        set((state) => {
          if (state.openPaths.includes(path)) return state;
          const next = [...state.openPaths, path];
          if (next.length <= MAX_OPEN_TABS) return { openPaths: next };
          const removableIndex = next.findIndex((item) => item !== HOME_PATH);
          next.splice(removableIndex, 1);
          return { openPaths: next };
        });
      },
      refreshTab: (path) => set((state) => ({
        refreshTokens: { ...state.refreshTokens, [path]: (state.refreshTokens[path] ?? 0) + 1 },
      })),
      closeTab: (path) => {
        if (path === HOME_PATH) return;
        set((state) => ({
          openPaths: state.openPaths.filter((item) => item !== path),
          recentlyClosed: [path, ...state.recentlyClosed.filter((item) => item !== path)].slice(0, MAX_RECENT_TABS),
        }));
      },
      closeLeft: (path) => set((state) => {
        const index = state.openPaths.indexOf(path);
        if (index <= 1) return state;
        const closed = state.openPaths.slice(1, index);
        return {
          openPaths: state.openPaths.filter((item) => !closed.includes(item)),
          recentlyClosed: [...closed.reverse(), ...state.recentlyClosed.filter((item) => !closed.includes(item))].slice(0, MAX_RECENT_TABS),
        };
      }),
      closeRight: (path) => set((state) => {
        const index = state.openPaths.indexOf(path);
        if (index < 0 || index === state.openPaths.length - 1) return state;
        const closed = state.openPaths.slice(index + 1).filter((item) => item !== HOME_PATH);
        return {
          openPaths: state.openPaths.filter((item) => !closed.includes(item)),
          recentlyClosed: [...closed.reverse(), ...state.recentlyClosed.filter((item) => !closed.includes(item))].slice(0, MAX_RECENT_TABS),
        };
      }),
      closeOthers: (path) => set((state) => {
        const keep = path === HOME_PATH ? [HOME_PATH] : [HOME_PATH, path];
        const closed = state.openPaths.filter((item) => !keep.includes(item) && item !== HOME_PATH);
        return {
          openPaths: keep,
          recentlyClosed: [...closed.reverse(), ...state.recentlyClosed.filter((item) => !closed.includes(item))].slice(0, MAX_RECENT_TABS),
        };
      }),
      closeAll: () => set((state) => ({
        openPaths: [HOME_PATH],
        recentlyClosed: [
          ...state.openPaths.filter((item) => item !== HOME_PATH).reverse(),
          ...state.recentlyClosed,
        ].slice(0, MAX_RECENT_TABS),
      })),
      reopenLastClosed: () => {
        const [path, ...rest] = get().recentlyClosed;
        if (!path || !isKnownPath(path)) return null;
        set((state) => ({
          openPaths: state.openPaths.includes(path) ? state.openPaths : [...state.openPaths, path],
          recentlyClosed: rest,
        }));
        return path;
      },
      moveTab: (path, direction) => set((state) => {
        const currentIndex = state.openPaths.indexOf(path);
        if (currentIndex < 0 || path === HOME_PATH) return state;
        const targetIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 1 || targetIndex >= state.openPaths.length) return state;
        const next = [...state.openPaths];
        [next[currentIndex], next[targetIndex]] = [next[targetIndex]!, next[currentIndex]!];
        return { openPaths: next };
      }),
      pruneTabs: (allowedPaths) => set((state) => {
        const allowed = new Set([HOME_PATH, ...allowedPaths]);
        const removed = state.openPaths.filter((path) => !allowed.has(path));
        if (!removed.length && state.recentlyClosed.every((path) => allowed.has(path))) return state;
        return {
          openPaths: state.openPaths.filter((path) => allowed.has(path)),
          recentlyClosed: state.recentlyClosed.filter((path) => allowed.has(path)),
          refreshTokens: Object.fromEntries(Object.entries(state.refreshTokens).filter(([path]) => allowed.has(path))),
        };
      }),
    }),
    {
      name: 'invoice-workspace-tabs',
      storage: createJSONStorage(() => sessionStorage),
      partialize: ({ openPaths, recentlyClosed }) => ({ openPaths, recentlyClosed }),
      merge: (persisted, current) => {
        const saved = persisted as Partial<NavigationState>;
        const validPaths = (saved.openPaths ?? []).filter(isKnownPath);
        return {
          ...current,
          openPaths: [HOME_PATH, ...validPaths.filter((path) => path !== HOME_PATH)].slice(0, MAX_OPEN_TABS),
          recentlyClosed: (saved.recentlyClosed ?? []).filter(isKnownPath).slice(0, MAX_RECENT_TABS),
        };
      },
    },
  ),
);
